import { Component, OnInit, signal, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MenuItem, ReportBugService } from '@card-software/library';
import {
  GlobalMessageUtil,
  MasterDataService,
  RolesBootstrapService,
  Site,
  SiteService,
  SiteBootstrapService,
} from '@card-software/nexus-utilities';
import { UserAuthenticationService } from './services/user-authentication.service';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import _ from 'lodash';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private router = inject(Router);
  private userAuthenticationService = inject(UserAuthenticationService);
  private translate = inject(TranslateService);
  private siteBootstrapService = inject(SiteBootstrapService);
  private messageService = inject(MessageService);
  private rolesBootstrapService = inject(RolesBootstrapService);

  title = 'asset-ui';
  isLoginPage = false;
  readonly environment = environment.environment;
  languageOptions = signal(['es', 'en']);

  menuItems: MenuItem[] = [];

  languageSelection = new FormControl('en');

  constructor() {
    GlobalMessageUtil.init(this.messageService);
    const token = this.userAuthenticationService.getStoredJwtToken();
    if (token) {
      MasterDataService.initialize(environment.proxyUrls.itemApi, token);
      ReportBugService.initialize(environment.proxyUrls.itemApi, token);
      this.rolesBootstrapService.initializeRoles(token);
      this.siteBootstrapService.initializeSites(token);
    }

    // set the default language to English
    this.translate.setDefaultLang('en');

    // validate if a page is login
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isLoginPage = _.includes(event.url, '/login');
      }
    });
  }

  ngOnInit(): void {
    this.loadMenuItems();

    this.languageSelection.valueChanges.subscribe((lang) => {
      this.translate.use(lang ?? 'en');
      this.loadMenuItems();
    });
  }

  loadMenuItems(): void {
    const translationKeys = {
      assets: 'Side_bar.Assets',
      admin: 'Side_bar.Administration',
      assetDetails: 'Side_bar.AssetDetails',
      sites: 'Side_bar.Sites',
    };

    this.translate.get(Object.values(translationKeys)).subscribe((translations) => {
      this.menuItems = [
        {
          displayName: translations[translationKeys.assets],
          routerLink: '/assets',
          redirect: true,
        },
        {
          displayName: translations[translationKeys.admin],
          routerLink: '/admin',
          redirect: false,
          children: [
            {
              displayName: translations[translationKeys.assetDetails],
              routerLink: 'admin/assets-details',
              redirect: true,
            },
            {
              displayName: translations[translationKeys.sites],
              routerLink: 'admin/sites',
              redirect: true,
            },
          ],
        },
      ];
    });
  }

  get isLoggedIn() {
    return this.userAuthenticationService.isUserAuthenticated;
  }

  get userLite() {
    return this.userAuthenticationService.userLite;
  }

  get userToken() {
    return this.userAuthenticationService.getStoredJwtToken() || '';
  }

  get showSidebar(): boolean {
    const isLoginPage = this.router.url.includes('/login');
    return this.userAuthenticationService.isUserAuthenticated && !isLoginPage;
  }

  get sites(): Site[] {
    return SiteService.getSites();
  }

  get selectedSiteId(): string {
    return SiteService.getSelectedSiteId() || '';
  }

  onSiteChanged(siteId: string) {
    SiteService.setSelectedSite(siteId);
  }

  onLogout() {
    this.userAuthenticationService.logout();
  }
}
